import type { BadgeTone } from '@zarya/ui';
import { t } from '@app/i18n';
import { RISK_TOM, riskLabel } from '@app/risk';
import type { AgentState, Resource, RiscoExibido, KindResource } from './Resources.api';

/**
 * Vocabulário de T6/T7 — rótulos do inventário do protótipo §2.7 e §4.4.
 * Mesmo papel do `identityVocab.ts` de T4: a tela não digita rótulo solto.
 */

/** Tom do badge de tipo (§2.7): `mcp` (info) / `api endpoint` (neutral). */
const KIND_TOM: Record<KindResource, BadgeTone> = {
  mcp: 'primary',
  api: 'neutral',
  // Fase 2 (aim-co1.3): o recurso agente — uma API `a2a/<caminho>` do gateway (a borda saiu no aim-cpj).
  agent: 'success',
};

/**
 * Badge de tipo. Função, e não tabela: o rótulo vem do catálogo, e uma tabela de
 * módulo é montada na importação — na troca de idioma ela continuaria mostrando
 * o idioma anterior sem nada ficar vermelho.
 */
export function kind(valor: KindResource): { rotulo: string; tone: BadgeTone } {
  return { rotulo: t(`resources.kind.${valor}`), tone: KIND_TOM[valor] };
}

/**
 * Risco declarado pelo dono (D4) — **nunca inferido pelo nome** (FR-032).
 *
 * Rótulo e cor vêm de `@app/risco`, a fonte única: risco é atributo do catálogo,
 * não da tela, e quem exibe não decide a cor dele. Aqui só se agrupa nos pares
 * `{rotulo, tone}` que as células de T6/T7 consomem.
 */
export function risk(valor: RiscoExibido): { rotulo: string; tone: BadgeTone } {
  return { rotulo: riskLabel(valor), tone: RISK_TOM[valor] };
}

/** Chip `descoberta via` (§4.4) — como as tools do resource foram descobertas. */
export function discoveredVia(resource: Resource): string {
  if (resource.kind === 'api') return t('resources.discoveredVia.api');
  return resource.origin === 'apim'
    ? t('resources.discoveredVia.mcpViaGateway')
    : t('resources.discoveredVia.mcp');
}

/** Banner fixo do drawer de resource manual (FR-034, cenário 3.9) — literal do PRD. */
export function bannerManual(): string {
  return t('resources.bannerManual');
}

/**
 * Aviso da tool em resource manual (FR-034, cenário 3.9).
 *
 * Mora aqui, e não no `ToolRow`, pela mesma razão que este arquivo existe: um
 * arquivo de componente só exporta componentes
 * (`react-refresh/only-export-components`). Era `const` no `ToolRow` e virou
 * função ao passar pelo catálogo — e função exportada de lá acende o lint.
 */
export function withoutEnforcement(): string {
  return t('resources.toolRow.withoutEnforcement');
}

/**
 * Tooltip de **Importar do gateway** desabilitado (FR-028, cenário 3.12).
 *
 * `apim_pronto` é o **cache** da última leitura de `GET /config` no middleware, e
 * cache vazio responde `false` (o estado seguro). Ou seja, o botão nasce
 * desabilitado em dois casos diferentes: o grupo do gateway não está configurado **ou**
 * o console ainda não leu a configuração — e um booleano só não os distingue.
 *
 * O texto anterior ("configure o grupo APIM em Infraestrutura") afirmava o
 * primeiro. Logo depois de um `RESET=1` — que é como a demonstração começa — o
 * caso verdadeiro é o segundo: o operador ia a Infraestrutura, encontrava os seis
 * grupos "pronto" e ficava sem saber o que fazer. Abrir a tela enche o cache e o
 * botão habilita sozinho, o que parecia mágica. Esta frase é verdadeira nos dois
 * estados e leva ao mesmo lugar, que resolve os dois.
 */
export function gatewayNotReadyTip(): string {
  return t('resources.gatewayNotReady.tip');
}

// --- fase 2 (aim-co1.3): o recurso agente --------------------------------------------

/** Tom do estado do agente (protótipo aprovado, T6): pronto é sucesso; sem destino confiável, o PDP nega. */
const AGENT_STATE_TONE: Record<AgentState, BadgeTone> = {
  ready: 'success',
  no_route: 'warning',
  card_unreadable: 'warning',
  untrusted_target: 'danger',
};

/**
 * A chave do catálogo de cada estado. O valor do contrato é snake_case (`no_route`) e a chave do catálogo é EN
 * camelCase (`keyShape.test.ts`): o mapa é explícito, e não uma conversão de caixa, para o compilador acusar um
 * estado novo sem rótulo.
 */
const AGENT_STATE_KEY: Record<AgentState, string> = {
  ready: 'ready',
  no_route: 'noRoute',
  card_unreadable: 'cardUnreadable',
  untrusted_target: 'untrustedTarget',
};

/** Badge do estado de um agente. Função pelo mesmo motivo de `kind`: o rótulo vem do catálogo. */
export function agentState(state: AgentState): { rotulo: string; tone: BadgeTone } {
  return { rotulo: t(`resources.agent.state.${AGENT_STATE_KEY[state]}`), tone: AGENT_STATE_TONE[state] };
}

/**
 * A coluna «Estado» de T6 para qualquer recurso: o estado do agente, ou — num MCP server e numa API — `ativo` e
 * `removido do gateway`, que é o que a lista sabia dizer antes.
 */
export function resourceState(resource: Resource): { rotulo: string; tone: BadgeTone } {
  if (resource.agent) return agentState(resource.agent.state);
  return resource.removed_from_gateway
    ? { rotulo: t('resources.state.removedFromGateway'), tone: 'warning' }
    : { rotulo: t('resources.state.active'), tone: 'success' };
}

/**
 * O caminho no gateway, como as telas o mostram: `/a2a/<caminho>/`. Nunca o uuid (regra 2 do protótipo): é o
 * caminho que os agentes chamam. A API que o serve, `a2a/<caminho>`, é a do `apiOf` (`AgentModel.ts`).
 */
export function agentPath(path: string): string {
  return `/a2a/${path}/`;
}

